// page-backlog-review-run.ts — Ce que l'écran de revue montre d'une passe.
//
// Tranche extraite de `page-backlog-review.ts` le 2026-08-26, quand le module a
// franchi le plafond de 1300 lignes du cliquet de taille. Le découpage n'est pas
// arbitraire : tout ce qui est ici est PUR — des fonctions qui reçoivent un run
// et rendent du HTML, plus un gel qui ne touche que le DOM. Le suivi lui-même
// (polling, modale d'attente, annulation) reste dans l'écran, parce qu'il
// dépend de son état et de ses rechargements.
//
// Une règle porte les trois rendus : **la popup dit la progression, le bandeau
// dit l'état**. Le bandeau n'a donc ni barre ni bouton d'annulation — il
// survivrait mal à la fermeture de la popup en la dupliquant.

export interface RunLike {
  id: number;
  status: string;
  provenance: string;
  steps_done: number;
  steps_total: number | null;
  started_at: string | null;
  error_code: string | null;
}

/** « 14:32 » — l'heure de démarrage, telle qu'on la lit. */
export function hhmm(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('fr-FR', { timeStyle: 'short' });
}

export function isRunning(run: RunLike | null | undefined): boolean {
  return run?.status === 'running';
}

/** Le corps de la popup d'attente : progression, étape, sortie. */
export function runModalBody(run: RunLike): string {
  const pct = run.steps_total ? Math.round((100 * (run.steps_done || 0)) / run.steps_total) : 0;
  const step =
    run.steps_total && run.steps_total > 0
      ? esc(
          t('review.run.step', {
            done: String(run.steps_done || 0),
            total: String(run.steps_total),
          }),
        )
      : '';
  return `<div aria-live="polite">
      <p>${esc(t('review.run.running'))} <span class="text-muted text-xs">${esc(t('review.run.startedAt', { time: hhmm(run.started_at) }))}</span></p>
      <div style="height:6px;background:var(--bg3);border-radius:3px;margin:10px 0">
        <div style="height:6px;width:${pct}%;background:var(--accent);border-radius:3px;transition:width .3s"></div>
      </div>
      <div class="text-xs text-muted">${step}</div>
      <p class="text-xs text-muted" style="margin-top:10px">${esc(t('review.run.leave'))}</p>
      <button type="button" id="btn-run-abort" class="btn btn-secondary btn-sm"
        style="margin-top:8px">${esc(t('review.run.abort'))}</button>
    </div>`;
}

/** Le rappel « analyse en cours », en tête de page.
 *
 * Dépouillé à dessein : ni barre de progression ni annulation — la popup les
 * porte. Le bandeau, lui, SURVIT à sa fermeture et à un retour sur la revue,
 * et c'est tout son intérêt : sans lui, une revue rouverte pendant une analyse
 * n'affichait rien et ses boutons semblaient simplement cassés.
 */
export function runBanner(run: RunLike | null | undefined): string {
  if (!isRunning(run)) return '';
  return `<div class="card" role="status" aria-live="polite"
      style="padding:10px;margin-bottom:8px;border-left:3px solid var(--warn)">
      <strong>${esc(t('review.run.running'))}</strong>
      <span class="text-muted text-xs"> — ${esc(t('review.run.frozen'))}</span>
      <button class="btn btn-secondary btn-sm" id="btn-run-reopen" style="margin-left:8px">
        ${esc(t('review.run.reopen'))}
      </button>
    </div>`;
}

// Tout ce qui ÉCRIT, et qu'une passe en cours rendrait incertain. Les filtres
// de pile et de famille en font partie : ils déclenchent un rendu, et un rendu
// au milieu d'une réécriture montrerait une liste à moitié remplacée.
const FROZEN_SELECTORS = [
  '#btn-present',
  '#btn-ask',
  '#btn-iterate-menu',
  '#btn-params-save',
  '[data-act]',
  '[data-flag]',
  '[data-note]',
  '[data-board-pile]',
  '[data-board-flag]',
  '[data-board-note]',
  '[data-pile]',
  '[data-family]',
  '[data-flagged]',
].join(',');

/** Gèle tout ce qui écrirait pendant qu'une passe tourne.
 *
 * Une itération réécrit les recommandations : arbitrer par-dessus, c'est
 * décider sur une liste qui va changer sous les doigts, et risquer de perdre le
 * geste. On désactive donc, plutôt que de laisser cliquer et d'échouer après.
 */
export function freezeControls(run: RunLike | null | undefined): void {
  if (!isRunning(run)) return;
  document.querySelectorAll<HTMLButtonElement>(FROZEN_SELECTORS).forEach((el) => {
    el.disabled = true;
    el.title = t('review.run.frozen');
  });
}

// Chaque cause d'échec a son message et son action utile. Les rabattre toutes
// sur « l'analyse n'a pas abouti » gaspille précisément le diagnostic que la
// discrimination de troncature a servi à produire.
const RUN_ERROR_KEYS: Record<string, string> = {
  lost: 'review.run.lost',
  truncated: 'review.run.truncated',
  unparsable: 'review.run.unparsable',
  provider_unavailable: 'review.run.providerDown',
  internal: 'review.run.internal',
};

export function runError(run: RunLike | null): string {
  if (!run || run.status === 'running' || run.status === 'succeeded') return '';
  const key =
    run.status === 'cancelled'
      ? 'review.run.cancelled'
      : RUN_ERROR_KEYS[run.error_code || ''] || 'review.run.failed';
  // Réessayer relance LA passe qui a échoué. Rejouer une passe procédurale
  // après un échec IA ne réparerait rien et masquerait le problème derrière un
  // résultat sans rapport — en laissant croire que la relance a fonctionné.
  const ai = run.provenance === 'ai_ornexia';
  const retryId = ai ? 'btn-retry-ai' : 'btn-iterate';
  return `<div class="error" style="margin-bottom:8px">${esc(t(key))}
      <button class="btn btn-secondary btn-sm" id="${retryId}" style="margin-left:8px">
        ${esc(t('review.run.retry'))}
      </button>
    </div>`;
}
